import React, { useEffect, useState } from "react";
import { api, money } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Briefcase, Building2, Phone, Mail, MapPin } from "lucide-react";

const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition";
const EMPTY = { name: "", contact_name: "", phone: "", email: "", address: "", notes: "" };
const INV_COLORS = { approved: "bg-blue-50 text-blue-700", invoiced: "bg-blue-50 text-blue-700", executed: "bg-amber-50 text-amber-700", billed: "bg-orange-50 text-orange-700", paid: "bg-emerald-50 text-emerald-700" };

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

export default function Clients() {
  const { t } = useLang();
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("jobs");

  const load = () => api.get("/clients").then(setClients).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c, e) => { e.stopPropagation(); setEditing(c); setForm({ ...EMPTY, ...c }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error(t("nameRequired"));
    const payload = { name: form.name, contact_name: form.contact_name, phone: form.phone, email: form.email, address: form.address, notes: form.notes };
    try {
      if (editing) await api.put(`/clients/${editing.id}`, payload);
      else await api.post("/clients", payload);
      toast.success(t("saved"));
      setOpen(false); load();
    } catch (err) {
      toast.error(err?.response?.status === 409 ? t("clientExists") : t("error"));
    }
  };

  const remove = async (c, e) => {
    e.stopPropagation();
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await api.del(`/clients/${c.id}`);
      toast.success(t("deleted")); load();
    } catch (err) { toast.error(err?.response?.status === 409 ? t("clientHasProps") : t("error")); }
  };

  const openDetail = async (c) => {
    setTab("jobs");
    try {
      const d = await api.get(`/clients/${c.id}/overview`);
      setDetail(d);
    } catch { toast.error(t("error")); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">{t("clients")}</h1>
        <button data-testid="new-client-btn" onClick={openNew} className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white text-sm font-medium px-4 py-2.5 hover:bg-slate-800 transition-colors">
          <Plus className="w-4 h-4" /> {t("newClient")}
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-500">
                <th className="text-left px-4 py-3">{t("client")}</th>
                <th className="text-left px-4 py-3">{t("contact")}</th>
                <th className="text-center px-4 py-3">{t("properties")}</th>
                <th className="text-center px-4 py-3">{t("jobsTab")}</th>
                <th className="text-right px-4 py-3">{t("invoicedTotal")}</th>
                <th className="text-right px-4 py-3">{t("paidTotal")}</th>
                <th className="text-right px-4 py-3">{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id} data-testid={`client-row-${c.id}`} onClick={() => openDetail(c)} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer">
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900 flex items-center gap-2"><Briefcase className="w-3.5 h-3.5 text-slate-400" />{c.name}</div>
                    {c.address && <div className="text-xs text-slate-400 ml-5">{c.address}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {c.contact_name && <div className="font-medium text-slate-800">{c.contact_name}</div>}
                    {c.phone && <div>{c.phone}</div>}
                    {c.email && <div className="text-slate-400">{c.email}</div>}
                    {!c.contact_name && !c.phone && !c.email && <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-center"><span className="text-xs font-semibold text-slate-600 bg-slate-100 rounded-full px-2.5 py-1">{c.properties_count || 0}</span></td>
                  <td className="px-4 py-3 text-center"><span className="text-xs font-semibold text-blue-700 bg-blue-50 rounded-full px-2.5 py-1">{c.jobs_count || 0}</span></td>
                  <td className="px-4 py-3 text-right font-medium tabular-nums">{money(c.invoiced_total || 0)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-emerald-700 font-medium">{money(c.paid_total || 0)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <button data-testid={`edit-client-${c.id}`} onClick={(e) => openEdit(c, e)} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900"><Pencil className="w-4 h-4" /></button>
                      <button data-testid={`delete-client-${c.id}`} onClick={(e) => remove(c, e)} className="p-1.5 rounded-md text-slate-500 hover:bg-red-50 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {clients.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">{t("noClients")}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div data-testid="client-modal" className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">{editing ? t("editClient") : t("newClient")}</h2>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("name")} *</label>
                <input data-testid="client-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("contactName")}</label>
                  <input data-testid="client-contact" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("phone")}</label>
                  <input data-testid="client-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("email")}</label>
                <input data-testid="client-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("address")}</label>
                <input data-testid="client-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("notes")}</label>
                <textarea data-testid="client-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className={inputCls} />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200">
              <button onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm font-medium border border-slate-300 hover:bg-slate-50">{t("cancel")}</button>
              <button data-testid="client-save" onClick={save} className="rounded-lg px-4 py-2 text-sm font-medium bg-slate-900 text-white hover:bg-slate-800">{t("save")}</button>
            </div>
          </div>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4 bg-black/40 backdrop-blur-sm" onClick={() => setDetail(null)}>
          <div data-testid="client-detail-modal" className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2"><Briefcase className="w-5 h-5 text-slate-400" />{detail.client.name}</h2>
                <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-3">
                  {detail.client.contact_name && <span>{detail.client.contact_name}</span>}
                  {detail.client.phone && <a href={`tel:${detail.client.phone}`} className="text-blue-600 inline-flex items-center gap-1"><Phone className="w-3 h-3" />{detail.client.phone}</a>}
                  {detail.client.email && <a href={`mailto:${detail.client.email}`} className="text-slate-500 inline-flex items-center gap-1"><Mail className="w-3 h-3" />{detail.client.email}</a>}
                  {detail.client.address && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{detail.client.address}</span>}
                </div>
              </div>
              <button onClick={() => setDetail(null)} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 px-6 pt-5" data-testid="client-kpis">
              {[
                [t("jobsTab"), detail.summary.jobs, "text-slate-900"],
                [t("budget"), money(detail.summary.budget_total), "text-slate-900"],
                [t("real"), money(detail.summary.actual_total), detail.summary.actual_total > detail.summary.budget_total && detail.summary.budget_total > 0 ? "text-red-600" : "text-slate-900"],
                [t("invoicedTotal"), money(detail.summary.invoiced_total), "text-orange-600"],
                [t("paidTotal"), money(detail.summary.paid_total), "text-emerald-600"],
              ].map(([lbl, val, cls]) => (
                <div key={lbl} className="bg-slate-50 rounded-lg p-3">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{lbl}</div>
                  <div className={`text-lg font-bold tabular-nums ${cls}`}>{val}</div>
                </div>
              ))}
            </div>

            <div className="flex gap-2 px-6 pt-5">
              {["jobs", "invoices", "properties"].map((k) => (
                <button key={k} data-testid={`client-tab-${k}`} onClick={() => setTab(k)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                  {k === "jobs" ? `${t("jobsTab")} (${detail.jobs.length})` : k === "invoices" ? `${t("invoices")} (${detail.invoices.length})` : `${t("properties")} (${detail.properties.length})`}
                </button>
              ))}
            </div>

            <div className="p-6">
              {tab === "jobs" && (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-500">
                      <th className="text-left py-2">#</th>
                      <th className="text-left py-2">{t("title")}</th>
                      <th className="text-left py-2">{t("propertyCol")}</th>
                      <th className="text-left py-2">{t("status")}</th>
                      <th className="text-right py-2">{t("budget")}</th>
                      <th className="text-right py-2">{t("real")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.jobs.map((j) => (
                      <tr key={j.id} className="border-b border-slate-100">
                        <td className="py-2 text-xs font-mono text-slate-500">{j.number}</td>
                        <td className="py-2 font-medium text-slate-800">{j.title}</td>
                        <td className="py-2 text-xs text-slate-500">{j.property_name}{j.unit_name ? ` — ${j.unit_name}` : ""}</td>
                        <td className="py-2"><span className="text-[11px] font-medium bg-slate-100 text-slate-600 rounded-full px-2 py-0.5 capitalize">{t(j.status) || j.status}</span></td>
                        <td className="py-2 text-right tabular-nums">{money(j.budget)}</td>
                        <td className={`py-2 text-right tabular-nums font-medium ${j.actual > j.budget && j.budget > 0 ? "text-red-600" : "text-slate-800"}`}>{money(j.actual)}</td>
                      </tr>
                    ))}
                    {detail.jobs.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-slate-400">{t("noData")}</td></tr>}
                  </tbody>
                </table>
              )}
              {tab === "invoices" && (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-500">
                      <th className="text-left py-2">#</th>
                      <th className="text-left py-2">{t("title")}</th>
                      <th className="text-left py-2">{t("status")}</th>
                      <th className="text-right py-2">{t("totalLabel")}</th>
                      <th className="text-left py-2 pl-4">{t("paidOn")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.invoices.map((inv) => (
                      <tr key={inv.id} className="border-b border-slate-100">
                        <td className="py-2 text-xs font-mono text-slate-500">{inv.number}</td>
                        <td className="py-2 font-medium text-slate-800">{inv.title}</td>
                        <td className="py-2"><span className={`text-[11px] font-medium rounded-full px-2 py-0.5 ${INV_COLORS[inv.status] || "bg-slate-100 text-slate-600"}`}>{t(inv.status) || inv.status}</span></td>
                        <td className="py-2 text-right tabular-nums font-medium">{money(inv.total)}</td>
                        <td className="py-2 pl-4 text-xs text-slate-500">{inv.status === "paid" ? fmtDate(inv.paid_date) : "—"}</td>
                      </tr>
                    ))}
                    {detail.invoices.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">{t("noInvoices")}</td></tr>}
                  </tbody>
                </table>
              )}
              {tab === "properties" && (
                <div className="space-y-2">
                  {detail.properties.map((p) => (
                    <div key={p.id} className="flex items-center justify-between border border-slate-200 rounded-lg px-4 py-3">
                      <div>
                        <div className="font-medium text-slate-800 flex items-center gap-2"><Building2 className="w-4 h-4 text-slate-400" />{p.name}</div>
                        <div className="text-xs text-slate-400 ml-6">{p.address}{p.city ? `, ${p.city}` : ""}</div>
                      </div>
                      <span className="text-xs font-semibold text-slate-600 bg-slate-100 rounded-full px-2.5 py-1">{p.units_count} {t("unitsCount")}</span>
                    </div>
                  ))}
                  {detail.properties.length === 0 && <p className="py-8 text-center text-slate-400 text-sm">{t("noData")}</p>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
